import React, { useMemo } from 'react';
import { Card, Box, Typography } from '@mui/material';
import { COLOR, FUENTE } from './tokens';
import { formatearTipoTramite } from '../../../constants/tiposTramiteTurno';
import InfoGrafico from './InfoGrafico';
import { MOCK_VOLUMEN_POR_TIPO } from './mockDataEjemplos';

/**
 * Volumen de atenciones por tipo de trámite, a secas. A diferencia de
 * TramitesVolumenConversion, no muestra la tasa de conversión: el backfill histórico no
 * tiene trámites asociados, así que cruzarlo con conversión daría una lectura falsa
 * (mucho volumen "sin convertir" que en realidad nunca se midió).
 *
 * Todas las categorías van del mismo color a propósito — el tipo de trámite no tiene
 * semántica de bueno/malo, así que va COLOR.azul (ver tokens.js).
 * @param {{data: Array<{tipoTramite: string, cantidad: number, porcentaje: number}>, titulo?: string, subtitulo?: string}} props
 */
const VolumenPorTipo = ({ data, titulo = 'Atenciones por tipo de trámite', subtitulo, compacto = false }) => {
    const filas = useMemo(
        () => [...(data || [])].sort((a, b) => b.cantidad - a.cantidad),
        [data]
    );
    const max = useMemo(() => Math.max(1, ...filas.map((f) => f.cantidad)), [filas]);
    const total = useMemo(() => filas.reduce((acc, f) => acc + f.cantidad, 0), [filas]);

    return (
        <Card variant="outlined" sx={{
            position: compacto ? 'static' : 'relative', border: compacto ? 'none' : '1px solid #e2e8f0',
            borderRadius: compacto ? 0 : '12px', boxShadow: 'none', padding: compacto ? 0 : '20px 22px',
        }}>
            {!compacto && (
                <>
                    <InfoGrafico
                        titulo={titulo}
                        queMuestra="Te dice qué tipo de trámite concentra más volumen de atenciones en el período, sin mezclarlo con conversión (que acá no se puede medir de forma confiable)."
                        ejemploVisual={<VolumenPorTipo compacto data={MOCK_VOLUMEN_POR_TIPO} />}
                        comoLeerlo={[
                            'Cada fila es un tipo de trámite, ordenado de mayor a menor cantidad de atenciones.',
                            'El largo de la barra es relativo al tipo con más atenciones del período, no a un total fijo.',
                            'A la derecha, la cantidad absoluta y el % sobre el total.',
                            'Todas las barras van del mismo azul a propósito: acá el tipo de trámite no tiene un lado "bueno" o "malo", solo se compara volumen — a diferencia de "Volumen vs. conversión", este gráfico no cruza con la tasa de conversión porque el backfill histórico que también suma acá no tiene trámites asociados, y cruzarlo daría un % de conversión falso.',
                        ]}
                        analisis="ART concentra el mayor volumen (34.1%), pero los otros tres tipos están bastante parejos entre sí (20-24% cada uno) — no hay una categoría claramente residual. Si el objetivo es planificar dotación por tipo de trámite, ART es el que más pesa, pero achicar cualquiera de los otros tres liberaría una porción de carga comparable."
                    />
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 16, fontWeight: 700, color: COLOR.negro }}>{titulo}</Typography>
                    {subtitulo && (
                        <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto, marginBottom: '16px' }}>
                            {subtitulo}
                        </Typography>
                    )}
                </>
            )}

            {!filas.length ? (
                <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.grisTexto, paddingTop: '8px' }}>
                    No hay atenciones en el período elegido.
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: subtitulo ? 0 : '16px' }}>
                    {filas.map((f) => (
                        <Box key={f.tipoTramite} sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.negro, width: 150, flexShrink: 0 }}>
                                {formatearTipoTramite(f.tipoTramite)}
                            </Typography>
                            <Box sx={{ position: 'relative', flex: 1, height: 18 }}>
                                <Box sx={{ position: 'absolute', inset: 0, borderRadius: '4px', background: COLOR.gris }} />
                                <Box sx={{
                                    position: 'absolute', top: 0, bottom: 0, left: 0,
                                    width: `${(f.cantidad / max) * 100}%`,
                                    borderRadius: '4px', background: COLOR.azul,
                                }} />
                            </Box>
                            <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, fontWeight: 700, color: COLOR.negro, width: 48, textAlign: 'right', flexShrink: 0 }}>
                                {f.cantidad.toLocaleString('es-AR')}
                            </Typography>
                            <Typography sx={{ fontFamily: FUENTE, fontSize: 12, color: COLOR.grisTexto, width: 48, textAlign: 'right', flexShrink: 0 }}>
                                {total ? `${((f.cantidad / total) * 100).toFixed(1)}%` : '—'}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Card>
    );
};

export default VolumenPorTipo;
